/**
 * Therapist invite-client dialog. Generates a personal link the therapist
 * copies and shares manually (no email is sent automatically).
 *
 * Backend: POST /api/invitations/individual with assignedTherapistId=self
 * (the controller defaults to the creator when therapist role is calling).
 */

import React, { useEffect, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Button, Stack, Alert, MenuItem, IconButton, InputAdornment, Typography, Box
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import api from '../../services/api';

export default function InviteClientDialog({ open, onClose, token, onCreated }) {
  const [serviceGroups, setServiceGroups] = useState([]);
  const [serviceGroupId, setServiceGroupId] = useState('');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [created, setCreated] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!open) return;
    setError(null);
    setCreated(null);
    setName(''); setEmail(''); setNotes('');
    (async () => {
      try {
        const res = await api.get('/service-groups/my-groups?role=therapist', token);
        const groups = res?.data?.data || res?.data || [];
        setServiceGroups(Array.isArray(groups) ? groups : []);
        if (groups.length === 1) setServiceGroupId(groups[0]._id);
      } catch (err) {
        setError(err?.response?.data?.error || err?.message || 'Could not load service groups');
      }
    })();
  }, [open, token]);

  async function submit() {
    setError(null);
    if (!name || !email || !serviceGroupId) {
      setError('Name, email, and service group are required.');
      return;
    }
    setSubmitting(true);
    try {
      const res = await api.post('/invitations/individual', {
        invitedName: name,
        invitedEmail: email,
        serviceGroupId,
        notes,
        sendEmail: false
      }, token);
      const inv = res?.data?.invitation || res?.data;
      setCreated(inv);
      onCreated?.(inv);
    } catch (err) {
      setError(err?.response?.data?.message || err?.response?.data?.error || err?.message || 'Could not create invitation');
    } finally {
      setSubmitting(false);
    }
  }

  async function copyLink() {
    if (!created?.invitationUrl) return;
    try {
      await navigator.clipboard.writeText(created.invitationUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (_) {
      // Fallback: select-and-copy via prompt
      window.prompt('Copy this link:', created.invitationUrl);
    }
  }

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Invite a client</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

        {!created ? (
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Service group" select fullWidth required
              value={serviceGroupId}
              onChange={(e) => setServiceGroupId(e.target.value)}
              helperText={serviceGroups.length === 0 ? 'You are not a therapist in any service group yet.' : ''}
            >
              {serviceGroups.map((g) => (
                <MenuItem key={g._id} value={g._id}>{g.name}</MenuItem>
              ))}
            </TextField>
            <TextField label="Client name" fullWidth required value={name} onChange={(e) => setName(e.target.value)} />
            <TextField label="Client email" type="email" fullWidth required value={email} onChange={(e) => setEmail(e.target.value)} />
            <TextField
              label="Notes (optional)" fullWidth multiline minRows={2} maxRows={4}
              placeholder="e.g. Referred by Dr Patel; trauma-informed CBT"
              value={notes} onChange={(e) => setNotes(e.target.value)}
            />
            <Typography variant="caption" color="text.secondary">
              No email is sent automatically. After creating the invitation you&apos;ll get a personal link
              you can share with the client however you prefer (SMS, email, secure messenger).
            </Typography>
          </Stack>
        ) : (
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Alert severity="success">
              Invitation created for <strong>{created.invitedName}</strong> ({created.invitedEmail}).
            </Alert>
            <TextField
              label="Personal signup link"
              fullWidth
              value={created.invitationUrl || ''}
              InputProps={{
                readOnly: true,
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={copyLink} edge="end">
                      <ContentCopyIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                )
              }}
              helperText={copied ? 'Copied!' : 'Copy this link and share it with the client. The link expires per the default lifetime.'}
            />
            <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Typography variant="caption" color="text.secondary">
                Status: <strong>{created.status}</strong>
                {created.expiresAt && <> · Expires {new Date(created.expiresAt).toLocaleString()}</>}
              </Typography>
            </Box>
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{created ? 'Done' : 'Cancel'}</Button>
        {!created && (
          <Button variant="contained" onClick={submit} disabled={submitting}>
            {submitting ? 'Creating…' : 'Create invitation'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
